import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Send,
  Sparkles,
  Eraser,
  ThumbsUp,
  ThumbsDown,
  BotMessageSquare,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import "./chatbot.css";
import { useDispatch, useSelector } from "react-redux";
import { ROUTE_LINK_LOCATION } from "../../../constants/external-constants";
import { ChatbotActions } from "../../../store/common/chatbot/chatbot.reducer";
import { invoiceAuditRulesActions } from "../../../store/external/invoice/invoice/audit-table.reducer";
import { requestDetailsActions } from "../../../store/external/request-reducer/request-details.reducer";
import { createPortal } from "react-dom";

const ChatbotWidget = ({ disabled = false }) => {
  const dispatch = useDispatch();

  const isOpen = useSelector((state) => state.chatbot.isOpen);
  const aiResponse = useSelector((state) => state.chatbot.responceMessage || "");
  const hasSpecialTask = useSelector((state) => state.chatbot.hasSpecialTask);
  const isLoading = useSelector((state) => state.chatbot.isLoading);

  const actionKey = useSelector((state) => state.chatbot.actionKey);
  const actionLabel = useSelector((state) => state.chatbot.actionLabel);
  const actionUrl = useSelector((state) => state.chatbot.actionUrl);

  const followUpSuggestions = useSelector(
    (state) => state.chatbot.followUpSuggestions || []
  );
  const orderScope = useSelector((state) => state.chatbot.orderScope || null);

  const actionButtons = useSelector((state) => state.chatbot.actionButtons || []);

  const planComponentOptions = useSelector(
      (state) => state.chatbot.planComponentOptions || []
  );

  const backendSelectedPlanComponents = useSelector(
      (state) =>
          state.chatbot.selectedPlanComponents ||
          state.chatbot.wirelineUpdatePlanDraft?.selectedPlanComponents ||
          []
  );

  const auditId = useSelector((state) => state.chatbot.auditId || 0);
  const exceptionId = useSelector((state) => state.chatbot.exceptionId || 0);
  const invoiceId = useSelector((state) => state.chatbot.invoiceId || 0);
  const vendorId = useSelector((state) => state.chatbot.vendorId || 0);
  const circuitTypeId = useSelector((state) => state.chatbot.circuitTypeId || 0);
  const currency = useSelector((state) => state.chatbot.currency || 0);
    const isGenerateSummary = useSelector(
    (state) => state.chatbot.isGenerateSummary || false
  );
  const getInvoiceDetailId = useSelector(
    (state) => state.chatbot.getInvoiceDetailId || false
  );

  const currentAuditPageIndex = useSelector(
    (state) =>
      state.invoiceAuditRules?.invoiceAuditRulesItems?.Pagination?.PageIndex ??
      state.invoiceAuditRules?.invoiceAuditRulesItems?.Pagination?.PageNumber ??
      0
    );

  const step = useSelector((state) => state.chatbot.step);

  const getStepBase = (value) =>
      String(value || "")
          .split("\n")[0]
          .trim();

  const isStepBase = (value, expected) =>
        getStepBase(value) === expected;

  const SPECIAL_TASK_TEXT = "Generate summary report";

  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastAiRef = useRef({ text: null, key: null, url: null });

  // Safe portal lookup
  const portalEl =
    typeof document !== "undefined"
      ? document.getElementById("chatbot-portal")
      : null;

  /**
   * Normalizes an order identifier so links use numeric order ids when prefixed values are returned.
   */
  const normalizeOrderIdentifier = (token) => {
    if (!token) return token;

    const trimmed = String(token).trim();
    if (/^\d+$/i.test(trimmed)) return trimmed;

    const numericPart = trimmed.match(/(\d{6,})/);
    return numericPart && numericPart[1] ? numericPart[1] : trimmed;
  };

  const userStr = sessionStorage.getItem("User");
  let firstName = null;
  if (userStr && userStr !== "null" && userStr !== "undefined") {
    try {
      const user = JSON.parse(userStr);
      firstName = user?.Employee?.FirstName ?? null;
    } catch (err) {
      console.error(err);
    }
  }
  const userInitial = firstName?.[0]?.toUpperCase() || "U";

  const isTaskSubmitted = messages.some(
    (msg) => msg.text === SPECIAL_TASK_TEXT && msg.sender === "user"
  );

  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
    });
    };

  // Scroll immediately on open
  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom(false);
  }, [isOpen]);

  // Scroll on new messages / generating changes
  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom(true);
  }, [messages, isGenerating, isOpen]);

  /**
   * Build AI message when response arrives after user sends.
   * Prevent duplicates with lastAiRef.
   */

    const accessoryOptions = useSelector((state) => state.chatbot.accessoryOptions || []);
    const [isAccessorySubmitted, setIsAccessorySubmitted] = useState(false);
    const [accessoryMessageIds, setAccessoryMessageIds] = useState([]);
    const [selectedAccessories, setSelectedAccessories] = useState([]);
    const [selectedAccessoriesByMessageId, setSelectedAccessoriesByMessageId] = useState({});

    const quotationCheckboxOptions = useSelector(
        (state) => state.chatbot.quotationCheckboxOptions || []
    );

    const quotationCheckboxField = useSelector(
        (state) => state.chatbot.quotationCheckboxField || null
    );

    const backendSelectedQuotationCheckboxOptions = useSelector(
        (state) =>
            state.chatbot.selectedQuotationCheckboxOptions ||
            []
    );

    const [isQuotationCheckboxSubmitted, setIsQuotationCheckboxSubmitted] = useState(false);
    const [quotationCheckboxMessageIds, setQuotationCheckboxMessageIds] = useState([]);
    const [selectedQuotationCheckboxOptions, setSelectedQuotationCheckboxOptions] = useState([]);
    const [selectedQuotationCheckboxOptionsByMessageId, setSelectedQuotationCheckboxOptionsByMessageId] = useState({});

    const quotationCheckboxStepFields = {
        await_quotation_vendors: "vendors",
        await_quotation_circuit_types: "circuitTypes",
        await_quotation_contract_terms: "contractTerms",
        await_quotation_bandwidth: "bandwidths"
    };

    const quotationCheckboxLabels = {
        vendors: "Vendors",
        circuitTypes: "Circuit Types",
        contractTerms: "Contract Terms",
        bandwidths: "Bandwidth"
    };

    const hasQuotationCheckboxPayload =
        !!quotationCheckboxField &&
        Array.isArray(quotationCheckboxOptions) &&
        quotationCheckboxOptions.length > 0;

    const isQuotationCheckboxStep =
        !!quotationCheckboxStepFields[step] ||
        (
            step === "await_quotation_update_value" &&
            hasQuotationCheckboxPayload
        );

    const activeQuotationCheckboxField =
        quotationCheckboxStepFields[step] || quotationCheckboxField;

    const activeQuotationCheckboxLabel =
        quotationCheckboxLabels[activeQuotationCheckboxField] || "Options";

    useEffect(() => {
        if (isLoading) return;

        const trimmed = typeof aiResponse === "string" ? aiResponse.trim() : "";
        if (!trimmed) return;

        const dupe =
            lastAiRef.current.text === trimmed &&
            lastAiRef.current.key === actionKey &&
            lastAiRef.current.url === actionUrl;

      if (dupe) {
          setIsGenerating(false);
          return;
      }

        lastAiRef.current = { text: trimmed, key: actionKey, url: actionUrl };

        const isAccessorySelection =
            step === "await_accessory_select_accessory" &&
            trimmed.toLowerCase().includes("accessory");

        const isQuotationCheckboxSelection =
            (
                !!quotationCheckboxStepFields[step] ||
                (
                    step === "await_quotation_update_value" &&
                    hasQuotationCheckboxPayload
                )
            ) &&
            !!activeQuotationCheckboxField;

        const isPlanComponentSelection =
            isStepBase(step, "await_wireline_update_plan_plan_advisor_choice") &&
            Array.isArray(planComponentOptions) &&
            planComponentOptions.length > 0;

        setMessages((prev) => [
            ...prev,
            {
                id: Date.now(),
                text: trimmed,
                sender: "ai",
                isAccessorySelection,
                isQuotationCheckboxSelection,
                quotationCheckboxField: activeQuotationCheckboxField,
                quotationCheckboxOptions: Array.isArray(quotationCheckboxOptions)
                    ? quotationCheckboxOptions
                    : [],
                isPlanComponentSelection,
                actionKey,
                actionLabel,
                actionUrl,
                followUpSuggestions: Array.isArray(followUpSuggestions) ? followUpSuggestions : [],
                orderScope,
                actionButtons: Array.isArray(actionButtons) ? actionButtons : [],
            },
        ]);

        setIsGenerating(false);
    }, [
        aiResponse,
        isLoading,
        actionKey,
        actionLabel,
        actionUrl,
        followUpSuggestions,
        orderScope,
        actionButtons,
        isOpen,
        step,
        quotationCheckboxOptions,
        quotationCheckboxField,
        planComponentOptions,
    ]);


  const handleToggle = (status) => {
    dispatch(ChatbotActions.toggleChat(status));
  };

  const handleClear = () => {
    const generateSummaryFlag = hasSpecialTask ? true : false;

    dispatch(
      ChatbotActions.clearMessage(
        auditId,
        exceptionId,
        generateSummaryFlag,
        invoiceId,
        getInvoiceDetailId
      )
    );

    setIsGenerating(false);
    setInputValue("");
    setMessages([]);
    lastAiRef.current = { text: null, key: null, url: null };

    setSelectedAccessories([]);
    setIsAccessorySubmitted(false);
    setAccessoryMessageIds([]);

    setSelectedQuotationCheckboxOptions([]);
    setIsQuotationCheckboxSubmitted(false);
    setQuotationCheckboxMessageIds([]);
    setSelectedQuotationCheckboxOptionsByMessageId({});

    setSelectedPlanComponents([]);
    setIsPlanComponentSubmitted(false);
    setPlanComponentMessageIds([]);
    setSelectedPlanComponentsByMessageId({});
  };

    const handleSend = async (text) => {
        const messageText = text || inputValue;
        if (!messageText.trim()) return;

        const newUserMsg = { id: Date.now(), text: messageText, sender: "user" };
        setMessages((prev) => [...prev, newUserMsg]);
        setInputValue("");

        // Reset duplicate guard for each new user turn
        lastAiRef.current = { text: null, key: null, url: null };

        setIsGenerating(true);

        try {
            const res = await dispatch(
                ChatbotActions.sendMessage(
                    messageText,
                    auditId,
                    exceptionId,
                    isGenerateSummary,
                    invoiceId,
                    getInvoiceDetailId
                )
            );

            if ((res?.actionIntent || "").toLowerCase() === "create_dispute") {
                dispatch(
                    invoiceAuditRulesActions.getInvoiceAuditRules(
                        invoiceId,
                        currentAuditPageIndex
                    )
                );
            }

            // If backend explicitly returned an error-shaped payload, stop local spinner now.
            if (res?.error) {
                setIsGenerating(false);
            }
        } catch (err) {
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now() + 1,
                    text: "Sorry - something went wrong while processing your request. Please try again.",
                    sender: "ai",
                    actionKey: null,
                    actionLabel: null,
                    actionUrl: null,
                    followUpSuggestions: [],
                    orderScope: null,
                    actionButtons: [],
                },
            ]);
            setIsGenerating(false);
        }
    };

  /**
   * Normalizes AI-provided URLs and rewrites legacy request links to the current route format.
   * Also allows "plan_advisor:" deep links to pass through.
   */
  const resolveChatbotLink = (rawUrl) => {
    if (!rawUrl) return rawUrl;

    // keep special scheme as-is so our click handler can intercept it
    if (String(rawUrl).startsWith("plan_advisor:")) return String(rawUrl);

    // allow mail/tel
    if (/^(mailto:|tel:)/i.test(rawUrl)) return rawUrl;

    const dealName = sessionStorage.getItem("DealName");
    let normalizedUrl = String(rawUrl).trim();

    // If AI sends absolute URL, convert to app-relative path for rewriting,
    // but keep original if URL parsing fails.
    if (/^https?:\/\//i.test(normalizedUrl)) {
      try {
        const parsedUrl = new URL(normalizedUrl);
        normalizedUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      } catch (e) {
        return rawUrl;
      }
    }

    if (!normalizedUrl.startsWith("/")) {
      normalizedUrl = `/${normalizedUrl}`;
    }

    const queryOrHashIndex = normalizedUrl.search(/[?#]/);
    const queryAndHashSuffix = queryOrHashIndex >= 0
      ? normalizedUrl.substring(queryOrHashIndex)
      : "";

    let isQuotationLink = false;
    try {
      const parsedCurrentUrl = new URL(normalizedUrl, window.location.origin);
      isQuotationLink = String(parsedCurrentUrl.searchParams.get("isQuotation")).toLowerCase() === "true";
    } catch (e) {
      isQuotationLink = false;
    }

    // legacy order details route -> current request detail route
    const legacyOrderDetailsMatch = normalizedUrl.match(
      /\/external\/Orders\/Orders\/OrderDetails\/Order\/([^/?#]+)/i
    );
    if (legacyOrderDetailsMatch && legacyOrderDetailsMatch[1] && dealName) {
      const normalizedOrderId = normalizeOrderIdentifier(
        legacyOrderDetailsMatch[1]
      );
      const requestScope = isQuotationLink ? "QuotationRequest" : "AllRequest";
      return `/external/${dealName}/Requests/${requestScope}/RequestDetail/Order/${normalizedOrderId}${queryAndHashSuffix}`;
    }

    // request detail route without deal segment -> add deal segment
    const requestDetailsWithoutDealMatch = normalizedUrl.match(
      /\/external\/Requests\/(AllRequest|MyRequest|QuotationRequest|ApprovalRequest|CoverageRequest)\/RequestDetail\/Order\/([^/?#]+)/i
    );
    if (
      requestDetailsWithoutDealMatch &&
      requestDetailsWithoutDealMatch[1] &&
      requestDetailsWithoutDealMatch[2] &&
      dealName
    ) {
      const normalizedOrderId = normalizeOrderIdentifier(
        requestDetailsWithoutDealMatch[2]
      );
      return `/external/${dealName}/Requests/${requestDetailsWithoutDealMatch[1]}/RequestDetail/Order/${normalizedOrderId}${queryAndHashSuffix}`;
    }

    return normalizedUrl;
  };

  // Called from Plan dropdown link click
  const openPlanAdvisor = async (payload) => {
    dispatch(ChatbotActions.setContext("plan_advisor", payload, "plan_link"));
    dispatch(ChatbotActions.toggleChat(true));
    setIsGenerating(true);
    await dispatch(ChatbotActions.sendMessage("PLAN_ADVISOR_START", 0, 0, false, 0, false));
  };

  /**
   * Unified navigation handler:
   * - plan_advisor: opens plan advisor flow
   * - external http(s): opens new tab
   * - internal routes: SPA pushState + popstate
   * - order detail: sets selected request
   */
  const navigateResolvedLink = async (href) => {
    if (!href) return;

    // Special scheme: plan advisor
    if (String(href).startsWith("plan_advisor:")) {
      try {
        const json = decodeURIComponent(String(href).replace("plan_advisor:", ""));
        const payload = JSON.parse(json);
        await openPlanAdvisor(payload);
      } catch (err) {
        console.error("Invalid plan advisor payload", err);
      }
      return;
    }

    // If absolute external link, open it as-is
    if (/^https?:\/\//i.test(String(href))) {
      window.open(String(href), "_blank", "noopener,noreferrer");
      return;
    }

    const resolvedUrl = resolveChatbotLink(href);
    if (!resolvedUrl) return;

    const getRequestTypeIdFromUrl = (url) => {
      try {
        const parsedUrl = new URL(String(url), window.location.origin);
        const rawTypeId =
          parsedUrl.searchParams.get("requestTypeId") ||
          parsedUrl.searchParams.get("requestTypeID");
        const parsedTypeId = Number(rawTypeId);
        return Number.isInteger(parsedTypeId) && parsedTypeId > 0
          ? parsedTypeId
          : null;
      } catch (e) {
        return null;
      }
      };

      const getRequestTypeFromUrl = (url) => {
          try {
              const parsedUrl = new URL(String(url), window.location.origin);
              const requestType = parsedUrl.searchParams.get("requestType");

              return requestType && String(requestType).trim().length > 0
                  ? String(requestType).trim()
                  : "";
          } catch (e) {
              return "";
          }
      };

    const getStatusFromUrl = (url) => {
      try {
        const parsedUrl = new URL(String(url), window.location.origin);
        const statusText = parsedUrl.searchParams.get("status");
        return statusText && String(statusText).trim().length > 0
          ? String(statusText).trim()
          : "";
      } catch (e) {
        return "";
      }
    };

    const getOrderStatusIdFromUrl = (url) => {
      try {
        const parsedUrl = new URL(String(url), window.location.origin);
        const rawOrderStatusId = parsedUrl.searchParams.get("orderStatusId");
        const parsedOrderStatusId = Number(rawOrderStatusId);
        return Number.isInteger(parsedOrderStatusId) && parsedOrderStatusId > 0
          ? parsedOrderStatusId
          : null;
      } catch (e) {
        return null;
      }
    };

    const getIsQuotationFromUrl = (url) => {
      try {
        const parsedUrl = new URL(String(url), window.location.origin);
        return String(parsedUrl.searchParams.get("isQuotation")).toLowerCase() === "true";
      } catch (e) {
        return false;
      }
    };

    // If internal order request detail, set selectedItem in Redux
    const orderDetailMatch = String(resolvedUrl).match(/\/RequestDetail\/Order\/(\d+)/i);
    if (orderDetailMatch && orderDetailMatch[1]) {
      const isQuotationRequest = getIsQuotationFromUrl(resolvedUrl);
      const statusFromUrl = getStatusFromUrl(resolvedUrl);
      const orderStatusIdFromUrl = getOrderStatusIdFromUrl(resolvedUrl);
        const resolvedOrderId = Number(orderDetailMatch[1]);

        const selectedRequestContext = isQuotationRequest
            ? {
                QuotationRequestId: resolvedOrderId,

                // Some shared modal functions, including isShowSurvey,
                // still read OrderId even for quotation requests.
                OrderId: resolvedOrderId,
            }
            : {
                OrderId: resolvedOrderId,
            };
        const requestTypeId = getRequestTypeIdFromUrl(resolvedUrl);
        const requestTypeFromUrl = getRequestTypeFromUrl(resolvedUrl);
      if (requestTypeId) {
        selectedRequestContext.RequestTypeId = requestTypeId;
        }
        if (requestTypeFromUrl) {
            selectedRequestContext.RequestType = requestTypeFromUrl;

            // Add both common property shapes in case the modal expects
            // the manual quotation-row structure.
            selectedRequestContext.RequestSubType = {
                Key: requestTypeId || 0,
                Value: requestTypeFromUrl,
            };
        }
      if (statusFromUrl) {
        selectedRequestContext.Status = statusFromUrl;
      }
      if (orderStatusIdFromUrl) {
        selectedRequestContext.OrderStatusId = orderStatusIdFromUrl;
      }
      dispatch(requestDetailsActions.setSelectedRequest(selectedRequestContext, isQuotationRequest));
    }

    handleToggle(false);

    // SPA navigation
    window.history.pushState({}, "", resolvedUrl);

    // Keep router updates compatible across browsers
    try {
      window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
    } catch (e) {
      const fallbackEvent = document.createEvent("Event");
      fallbackEvent.initEvent("popstate", true, true);
      window.dispatchEvent(fallbackEvent);
    }
  };

    const handleChatbotAction = (action, invoiceID, invoiceAuditID, vendorId, circuitTypeId, currency, url) => {
    if (url) {
      navigateResolvedLink(url);
      return;
    }

    if (action === "CREATE_NEW_CIRCUIT") {
        const target = `${ROUTE_LINK_LOCATION.inventoryCreateCircuitLink}/${invoiceID}/${invoiceAuditID}`;

        window.open(
            new URL(target, window.location.origin).href,
            "_blank",
            "noopener,noreferrer"
        );

        return;
     
    }

    if (action === "CREATE_NEW_PLAN") {
        const target =
            `${ROUTE_LINK_LOCATION.financeCreatePlanCatalogueLink}/${vendorId}/${circuitTypeId}/${currency}`;

        window.open(
            new URL(target, window.location.origin).href,
            "_blank",
            "noopener,noreferrer"
        );

        return;
    }

    };

    const [isPlanComponentSubmitted, setIsPlanComponentSubmitted] = useState(false);
    const [planComponentMessageIds, setPlanComponentMessageIds] = useState([]);
    const [selectedPlanComponents, setSelectedPlanComponents] = useState([]);
    const [selectedPlanComponentsByMessageId, setSelectedPlanComponentsByMessageId] = useState({});

    const backendSelected = useSelector(
        (state) =>
            state.chatbot.selectedAccessories ||
            state.chatbot.accessoryDraft?.selectedAccessories ||
            []
    );

    useEffect(() => {
        if (step === "await_accessory_select_accessory") {
            const normalizedSelected = Array.isArray(backendSelected)
                ? backendSelected.map((x) => ({
                    id: x.id ?? x.accessoryId ?? x.AccessoryId,
                    label: x.label ?? x.accessoryName ?? x.AccessoryName,
                    cost: x.cost ?? x.Cost ?? 0
                }))
                : [];

            setSelectedAccessories(normalizedSelected);
            setIsAccessorySubmitted(false);
        }
    }, [step, backendSelected]);

    useEffect(() => {
        if (isStepBase(step, "await_wireline_update_plan_plan_advisor_choice") && planComponentOptions.length > 0) {
            const normalizedSelected = Array.isArray(backendSelectedPlanComponents) && backendSelectedPlanComponents.length > 0
                ? backendSelectedPlanComponents.map((x) => ({
                    id: x.id ?? x.planComponentId ?? x.PlanComponentId,
                    planComponentId: x.planComponentId ?? x.PlanComponentId ?? x.id,
                    label: x.label ?? x.componentName ?? x.ComponentName,
                    componentName: x.componentName ?? x.ComponentName ?? x.label,
                    cost: x.cost ?? x.ComponentCost ?? x.componentCost ?? 0,
                    currency: x.currency ?? x.Currency ?? planComponentOptions?.[0]?.currency ?? "USD"
                }))
                : planComponentOptions.map((x) => ({
                    id: x.id ?? x.planComponentId,
                    planComponentId: x.planComponentId ?? x.id,
                    label: x.label ?? x.componentName,
                    componentName: x.componentName ?? x.label,
                    cost: x.cost ?? 0,
                    currency: x.currency ?? "USD"
                }));

            setSelectedPlanComponents(normalizedSelected);
            setIsPlanComponentSubmitted(false);
        }
    }, [step, backendSelectedPlanComponents, planComponentOptions]);

    useEffect(() => {
        if (!isQuotationCheckboxStep) {
            return;
        }

        const normalizedSelected = Array.isArray(backendSelectedQuotationCheckboxOptions)
            ? backendSelectedQuotationCheckboxOptions.map((x) => ({
                id: x.id ?? x.key ?? x.Id ?? x.Key,
                label: x.label ?? x.value ?? x.Label ?? x.Value
            }))
            : [];

        setSelectedQuotationCheckboxOptions(normalizedSelected);
        setIsQuotationCheckboxSubmitted(false);
    }, [step, backendSelectedQuotationCheckboxOptions, isQuotationCheckboxStep]);

    useEffect(() => {
        const lastAiMessage = [...messages]
            .reverse()
            .find((m) => m.sender === "ai" && m.isPlanComponentSelection);

        if (lastAiMessage) {
            setPlanComponentMessageIds((prev) =>
                prev.includes(lastAiMessage.id)
                    ? prev
                    : [...prev, lastAiMessage.id]
            );
        }
    }, [messages]);

    useEffect(() => {
        const lastAiMessage = [...messages]
            .reverse()
            .find((m) => m.sender === "ai" && m.isAccessorySelection);

        if (lastAiMessage) {
            setAccessoryMessageIds((prev) =>
                prev.includes(lastAiMessage.id)
                    ? prev
                    : [...prev, lastAiMessage.id]
            );
        }
    }, [messages]);

    useEffect(() => {
        const lastAiMessage = [...messages]
            .reverse()
            .find((m) => m.sender === "ai" && m.isQuotationCheckboxSelection);

        if (lastAiMessage) {
            setQuotationCheckboxMessageIds((prev) =>
                prev.includes(lastAiMessage.id)
                    ? prev
                    : [...prev, lastAiMessage.id]
            );
        }
    }, [messages]);

  const widgetUi = (
    <div className="chat-widget-container font-sans">
      <div className={`chat-window shadow-lg ${isOpen ? "open" : ""}`}>
        <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-white">
          <div>
            <h5 className="mb-0 fw-bold text-dark">
              <span>AI</span>
              <span className="chatbot-secondary__title">TEM</span>
            </h5>
            <small className="sub-title text-muted d-flex align-items-center gap-1">
              Empowered by Azure Open AI <Sparkles size={12} className="text-primary" />
            </small>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm text-secondary text-decoration-none icon-btn-hover"
              title="Clear"
              onClick={handleClear}
              disabled={isLoading}
            >
              <Eraser size={18} />
            </button>
            <button
              className="btn btn-sm text-secondary text-decoration-none icon-btn-hover"
              title="Close"
              onClick={() => handleToggle(false)}
            >
              <X size={22} />
            </button>
          </div>
        </div>
        <div className="flex-grow-1 bg-light p-3 overflow-auto chat-body-scroll d-flex flex-column">
          {messages.length === 0 && !isGenerating && (
            <div className="mt-3 fade-in">
              <div className="mb-4">
                <p className="fs-5 fw-semibold text-dark mb-1">How can I help?</p>
                <div className="chat-message-header d-flex align-items-center gap-2 text-muted">
                  <span>Is this response helpful?</span>
                  <ThumbsUp size={12} style={{ cursor: "pointer" }} />
                  <ThumbsDown size={12} style={{ cursor: "pointer" }} />
                </div>
              </div>
            </div>
          )}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`d-flex mb-2 ${
                msg.sender === "user" ? "justify-content-end" : "justify-content-start"
              }`}
            >
              {msg.sender === "ai" && (
                <div className="chat-avatar chat-avatar-ai shadow-sm">
                  <BotMessageSquare size={18} />
                </div>
              )}
              <div className={`msg-bubble shadow-sm ${msg.sender === "user" ? "msg-user" : "msg-ai"}`}>
                <ReactMarkdown
                  components={{
                    a: ({ node, children, ...props }) => (
                      <a
                        href={resolveChatbotLink(props.href)}
                        onClick={(e) => {
                          e.preventDefault();
                          navigateResolvedLink(props.href);
                        }}
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                          {(() => {
                              const text = msg.text || "";

                              if (text.includes("ACCESSORY_SELECTED_JSON:")) {
                                  return text.substring(0, text.indexOf("ACCESSORY_SELECTED_JSON:"));
                              }

                              if (text.includes("QUOTATION_MULTISELECT_JSON:")) {
                                  return text.substring(0, text.indexOf("QUOTATION_MULTISELECT_JSON:"));
                              }

                              if (text.includes("PLAN_COMPONENT_SELECTED_JSON:")) {
                                  return text.substring(0, text.indexOf("PLAN_COMPONENT_SELECTED_JSON:"));
                              }
                              return text;
                          })()}
                </ReactMarkdown>
                {msg.sender === "ai" &&
                  ((msg.actionKey !== null && msg.actionKey !== undefined) || msg.actionUrl) && (
                    <button
                      key={`${msg.id}-action`}
                      className="chatbot-link"
                      onClick={() =>
                        handleChatbotAction(msg.actionKey, invoiceId, auditId, vendorId, circuitTypeId, currency, msg.actionUrl)
                      }
                    >
                      {msg.actionLabel}
                    </button>
                  )}
                {msg.sender === "ai" &&
                  !msg.orderScope &&
                  Array.isArray(msg.followUpSuggestions) &&
                  msg.followUpSuggestions.length > 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {msg.followUpSuggestions.map((suggestion, idx) => (
                        <button
                          key={`${msg.id}-suggestion-${idx}`}
                          className="btn suggestion-chip py-1 px-2 rounded-3"
                          onClick={() => handleSend(suggestion)}
                          disabled={isLoading}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                {msg.sender === "ai" &&
                  Array.isArray(msg.actionButtons) &&
                  msg.actionButtons.length > 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {msg.actionButtons.map((btn, idx) => (
                        <button
                          key={`${msg.id}-ab-${btn.key ?? idx}`}
                          className="btn suggestion-chip py-1 px-2 rounded-3"
                          onClick={() => handleSend(btn.sendText)}
                          disabled={isLoading}
                        >
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  )}
                      {msg.sender === "ai" &&
                          msg.isAccessorySelection &&
                          accessoryMessageIds.includes(msg.id) &&
                          (step === "await_accessory_select_accessory" || isAccessorySubmitted) &&
                          accessoryOptions.length > 0 &&
                          (() => {
                              const latestAccessoryMessageId =
                                  accessoryMessageIds.length > 0
                                      ? accessoryMessageIds[accessoryMessageIds.length - 1]
                                      : null;

                              const isLatestAccessoryMessage = msg.id === latestAccessoryMessageId;

                              const displaySelectedAccessories = isLatestAccessoryMessage
                                  ? selectedAccessories
                                  : selectedAccessoriesByMessageId[msg.id] || [];

                          return (
                              <div className="accessory-options">
                                  {accessoryOptions.map((item) => {
                                          const checked = displaySelectedAccessories.some(
                                              (x) => String(x.id) === String(item.id)
                                          );

                                          return (
                                              <label key={item.id} className="accessory-option">
                                                  <input
                                                      type="checkbox"
                                                      checked={checked}
                                                      disabled={!isLatestAccessoryMessage || isAccessorySubmitted}
                                                      onChange={(e) => {
                                                          if (!isLatestAccessoryMessage || isAccessorySubmitted) return;

                                                          if (e.target.checked) {
                                                              setSelectedAccessories((prev) =>
                                                                  prev.some((x) => String(x.id) === String(item.id))
                                                                      ? prev
                                                                      : [...prev, item]
                                                              );
                                                          } else {
                                                              setSelectedAccessories((prev) =>
                                                                  prev.filter((x) => String(x.id) !== String(item.id))
                                                              );
                                                          }
                                                      }}
                                                  />

                                                  <span>{item.label}</span>

                                                  <strong>USD {Number(item.cost || 0).toFixed(2)}</strong>
                                              </label>
                                          );
                                      })}

                                      <div className="accessory-total">
                                          Total Order Cost: {accessoryOptions[0]?.currency || "USD"}{" "}
                                          {displaySelectedAccessories
                                              .reduce((sum, x) => sum + Number(x.cost || 0), 0)
                                              .toFixed(2)}
                                      </div>

                                      {selectedAccessories.length === 0 && isLatestAccessoryMessage && (
                                          <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
                                              Please select at least one accessory.
                                          </div>
                                      )}

                                      <button
                                          className="accessory-submit"
                                          disabled={
                                              selectedAccessories.length === 0 ||
                                              isAccessorySubmitted ||
                                              !isLatestAccessoryMessage
                                          }
                                          onClick={() => {
                                              if (selectedAccessories.length === 0) {
                                                  alert("Please select at least one accessory.");
                                                  return;
                                              }

                                              const message =
                                                  "Selected accessories:\n" +
                                                  selectedAccessories
                                                      .map((x) => `- ${x.label} | USD ${Number(x.cost || 0).toFixed(2)}`)
                                                      .join("\n") +
                                                  "\n\nACCESSORY_SELECTED_JSON:" +
                                                  JSON.stringify(
                                                      selectedAccessories.map((x) => ({
                                                          id: String(x.id),
                                                          accessoryId: String(x.id),
                                                          label: x.label,
                                                          accessoryName: x.label,
                                                          cost: Number(x.cost || 0)
                                                      }))
                                                  );

                                              setSelectedAccessoriesByMessageId((prev) => ({
                                                  ...prev,
                                                  [msg.id]: selectedAccessories
                                              }));

                                              setIsAccessorySubmitted(true);
                                              handleSend(message);
                                          }}
                                      >
                                          {isAccessorySubmitted && isLatestAccessoryMessage
                                              ? "Accessories Confirmed"
                                              : "Confirm Accessories"}
                                      </button>
                                  </div>
                              );
                          })()}

                      {msg.sender === "ai" &&
                          msg.isQuotationCheckboxSelection &&
                          quotationCheckboxMessageIds.includes(msg.id) &&
                          (() => {
                              const latestQuotationCheckboxMessageId =
                                  quotationCheckboxMessageIds.length > 0
                                      ? quotationCheckboxMessageIds[quotationCheckboxMessageIds.length - 1]
                                      : null;

                              const isLatestQuotationCheckboxMessage =
                              msg.id === latestQuotationCheckboxMessageId;

                          const messageQuotationCheckboxOptions =
                              Array.isArray(msg.quotationCheckboxOptions) && msg.quotationCheckboxOptions.length > 0
                                  ? msg.quotationCheckboxOptions
                                  : quotationCheckboxOptions;

                          const savedSelectedForMessage =
                              selectedQuotationCheckboxOptionsByMessageId[msg.id] || [];

                          const shouldRenderQuotationCheckbox =
                              isLatestQuotationCheckboxMessage
                                  ? messageQuotationCheckboxOptions.length > 0
                                  : savedSelectedForMessage.length > 0;

                          if (!shouldRenderQuotationCheckbox) {
                              return null;
                          }

                              const field = msg.quotationCheckboxField || activeQuotationCheckboxField;
                              const label = quotationCheckboxLabels[field] || "Options";

                          const displaySelectedQuotationCheckboxOptions =
                              isLatestQuotationCheckboxMessage
                                  ? selectedQuotationCheckboxOptions
                                  : savedSelectedForMessage;

                          const selectableQuotationOptions = messageQuotationCheckboxOptions || [];

                          const allQuotationOptionsSelected =
                              selectableQuotationOptions.length > 0 &&
                              selectableQuotationOptions.every((item) =>
                                  displaySelectedQuotationCheckboxOptions.some(
                                      (x) => String(x.id) === String(item.id)
                                  )
                              );

                          return (
                              <div className="accessory-options">
                                  <label className="accessory-option">
                                      <input
                                          type="checkbox"
                                          checked={allQuotationOptionsSelected}
                                          disabled={!isLatestQuotationCheckboxMessage || isQuotationCheckboxSubmitted}
                                          onChange={(e) => {
                                              if (!isLatestQuotationCheckboxMessage || isQuotationCheckboxSubmitted) return;

                                              if (e.target.checked) {
                                                  setSelectedQuotationCheckboxOptions(selectableQuotationOptions);
                                              } else {
                                                  setSelectedQuotationCheckboxOptions([]);
                                              }
                                          }}
                                      />

                                      <span>Select All</span>
                                  </label>

                                  {selectableQuotationOptions.map((item) => {
                                          const checked = displaySelectedQuotationCheckboxOptions.some(
                                              (x) => String(x.id) === String(item.id)
                                          );

                                          return (
                                              <label key={item.id} className="accessory-option">
                                                  <input
                                                      type="checkbox"
                                                      checked={checked}
                                                      disabled={!isLatestQuotationCheckboxMessage || isQuotationCheckboxSubmitted}
                                                      onChange={(e) => {
                                                          if (!isLatestQuotationCheckboxMessage || isQuotationCheckboxSubmitted) return;

                                                          if (e.target.checked) {
                                                              setSelectedQuotationCheckboxOptions((prev) => {
                                                                  const next = prev.some((x) => String(x.id) === String(item.id))
                                                                      ? prev
                                                                      : [...prev, item];

                                                                  const allSelected =
                                                                      selectableQuotationOptions.length > 0 &&
                                                                      selectableQuotationOptions.every((option) =>
                                                                          next.some((x) => String(x.id) === String(option.id))
                                                                      );

                                                                  return allSelected ? selectableQuotationOptions : next;
                                                              });
                                                          } else {
                                                              setSelectedQuotationCheckboxOptions((prev) =>
                                                                  prev.filter((x) => String(x.id) !== String(item.id))
                                                              );
                                                          }
                                                      }}
                                                  />

                                                  <span>{item.label}</span>
                                              </label>
                                          );
                                      })}

                                      {selectedQuotationCheckboxOptions.length === 0 && isLatestQuotationCheckboxMessage && (
                                          <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
                                              Please select at least one option.
                                          </div>
                                      )}

                                      <button
                                          className="accessory-submit"
                                          disabled={
                                              selectedQuotationCheckboxOptions.length === 0 ||
                                              isQuotationCheckboxSubmitted ||
                                              !isLatestQuotationCheckboxMessage
                                          }
                                          onClick={() => {
                                              if (selectedQuotationCheckboxOptions.length === 0) {
                                                  alert("Please select at least one option.");
                                                  return;
                                              }

                                              const message =
                                                  `Selected ${label}:\n` +
                                                  selectedQuotationCheckboxOptions
                                                      .map((x) => `- ${x.label}`)
                                                      .join("\n") +
                                                  "\n\nQUOTATION_MULTISELECT_JSON:" +
                                                  JSON.stringify(
                                                      selectedQuotationCheckboxOptions.map((x) => ({
                                                          id: String(x.id),
                                                          key: String(x.id),
                                                          label: x.label,
                                                          value: x.label
                                                      }))
                                                  );

                                              setSelectedQuotationCheckboxOptionsByMessageId((prev) => ({
                                                  ...prev,
                                                  [msg.id]: selectedQuotationCheckboxOptions
                                              }));

                                              setIsQuotationCheckboxSubmitted(true);
                                              handleSend(message);
                                          }}
                                      >
                                          {isQuotationCheckboxSubmitted && isLatestQuotationCheckboxMessage
                                              ? `${label} Confirmed`
                                              : `Confirm ${label}`}
                                      </button>
                                  </div>
                              );
                          })()}


                      {msg.sender === "ai" &&
                          msg.isPlanComponentSelection &&
                          planComponentMessageIds.includes(msg.id) &&
                          (isStepBase(step, "await_wireline_update_plan_plan_advisor_choice") || isPlanComponentSubmitted) &&
                          planComponentOptions.length > 0 &&
                          (() => {
                              const latestPlanComponentMessageId =
                                  planComponentMessageIds.length > 0
                                      ? planComponentMessageIds[planComponentMessageIds.length - 1]
                                      : null;

                              const isLatestPlanComponentMessage = msg.id === latestPlanComponentMessageId;

                              const displaySelectedPlanComponents = isLatestPlanComponentMessage
                                  ? selectedPlanComponents
                                  : selectedPlanComponentsByMessageId[msg.id] || [];

                              const currency = planComponentOptions[0]?.currency || "USD";

                              return (
                                  <div className="accessory-options">
                                      {planComponentOptions.map((item) => {
                                          const itemId = item.id ?? item.planComponentId;
                                          const checked = displaySelectedPlanComponents.some(
                                              (x) => String(x.id ?? x.planComponentId) === String(itemId)
                                          );

                                          return (
                                              <label key={itemId} className="accessory-option">
                                                  <input
                                                      type="checkbox"
                                                      checked={checked}
                                                      disabled={!isLatestPlanComponentMessage || isPlanComponentSubmitted}
                                                      onChange={(e) => {
                                                          if (!isLatestPlanComponentMessage || isPlanComponentSubmitted) return;

                                                          const normalizedItem = {
                                                              id: String(itemId),
                                                              planComponentId: Number(item.planComponentId ?? itemId),
                                                              label: item.label ?? item.componentName,
                                                              componentName: item.componentName ?? item.label,
                                                              cost: Number(item.cost || 0),
                                                              currency: item.currency || currency
                                                          };

                                                          if (e.target.checked) {
                                                              setSelectedPlanComponents((prev) =>
                                                                  prev.some((x) => String(x.id ?? x.planComponentId) === String(itemId))
                                                                      ? prev
                                                                      : [...prev, normalizedItem]
                                                              );
                                                          } else {
                                                              setSelectedPlanComponents((prev) =>
                                                                  prev.filter((x) => String(x.id ?? x.planComponentId) !== String(itemId))
                                                              );
                                                          }
                                                      }}
                                                  />
                                                  <span>{item.label ?? item.componentName}</span>
                                                  <strong>{currency} {Number(item.cost || 0).toFixed(2)}</strong>
                                              </label>
                                          );
                                      })}

                                      <div className="accessory-total">
                                          Total Plan Component Cost: {currency}{" "}
                                          {displaySelectedPlanComponents
                                              .reduce((sum, x) => sum + Number(x.cost || 0), 0)
                                              .toFixed(2)}
                                      </div>

                                      {selectedPlanComponents.length === 0 && isLatestPlanComponentMessage && (
                                          <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
                                              Please select at least one plan component.
                                          </div>
                                      )}

                                      <button
                                          className="accessory-submit"
                                          disabled={
                                              selectedPlanComponents.length === 0 ||
                                              isPlanComponentSubmitted ||
                                              !isLatestPlanComponentMessage
                                          }
                                          onClick={() => {
                                              if (selectedPlanComponents.length === 0) {
                                                  alert("Please select at least one plan component.");
                                                  return;
                                              }

                                              const message =
                                                  "Selected plan components:\n" +
                                                  selectedPlanComponents
                                                      .map((x) => `- ${x.label ?? x.componentName} | ${currency} ${Number(x.cost || 0).toFixed(2)}`)
                                                      .join("\n") +
                                                  "\n\nPLAN_COMPONENT_SELECTED_JSON:" +
                                                  JSON.stringify(
                                                      selectedPlanComponents.map((x) => ({
                                                          id: String(x.id ?? x.planComponentId),
                                                          planComponentId: Number(x.planComponentId ?? x.id),
                                                          label: x.label ?? x.componentName,
                                                          componentName: x.componentName ?? x.label,
                                                          cost: Number(x.cost || 0),
                                                          currency: x.currency || currency
                                                      }))
                                                  );

                                              setSelectedPlanComponentsByMessageId((prev) => ({
                                                  ...prev,
                                                  [msg.id]: selectedPlanComponents
                                              }));

                                              setIsPlanComponentSubmitted(true);
                                              handleSend(message);
                                          }}
                                      >
                                          {isPlanComponentSubmitted && isLatestPlanComponentMessage
                                              ? "Plan Components Confirmed"
                                              : "Confirm Plan Components"}
                                      </button>
                                  </div>
                              );
                          })()}

              </div>
              {msg.sender === "user" && (
                <div className="chat-avatar chat-avatar-user shadow-sm">{userInitial}</div>
              )}
            </div>
          ))}
          {isGenerating && (
            <div className="chat-card__container card border-0 shadow-sm p-3 mb-2">
              <div className="d-flex align-items-center gap-2">
                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                <span className="text-muted small fw-medium">Thinking...</span>
              </div>
            </div>
          )}
          {hasSpecialTask && !isTaskSubmitted && !isGenerating && (
            <div className="d-flex justify-content-end mb-3 mt-2 fade-in">
              <button
                className="btn suggestion-chip py-2 px-3 rounded-3 shadow-sm"
                onClick={() => handleSend(SPECIAL_TASK_TEXT)}
                disabled={isLoading}
              >
                Do you want to generate summary report?
              </button>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="p-3 border-top bg-white">
          <div className="position-relative mb-2">
            <textarea
              ref={inputRef}
              className="form-control chat-input rounded-4 pe-5"
              rows="2"
              placeholder="Type a message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (isLoading && e.key === "Enter") {
                  e.preventDefault();
                  return;
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            ></textarea>
            <div className="chat-input-send position-absolute top-50 end-0 translate-middle-y pe-2" style={{ pointerEvents: "none" }}>
              <button
                style={{ pointerEvents: "auto" }}
                className={`btn btn-sm btn-submit__message rounded-circle d-flex align-items-center justify-content-center ${
                  inputValue.trim() ? "aitem-gradient-bg" : "btn-light text-muted"
                }`}
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || isLoading}
              >
                <Send size={16} />
              </button>
            </div>
          </div>
          <div className="d-flex justify-content-center align-items-center ps-1">
            <small className="text-muted" style={{ fontSize: "10px" }}>
              Azure Open AI uses GPT-4o AI model. Always review content for mistakes
            </small>
          </div>
        </div>
      </div>
      <button
        className={`chat-trigger-btn aitem-gradient-bg ${isOpen ? "hide-btn" : ""}`}
        onClick={() => handleToggle(true)}
      >
        <Sparkles size={28} />
      </button>
    </div>
  );

  if (disabled) return null;

  if (!portalEl) return widgetUi;
  return createPortal(widgetUi, portalEl);
};

export default ChatbotWidget;